import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import {
  assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL, fromDateTimeInput, fullName,
  type TaskRecurrenceKey,
} from '../../features/tasks/tasksApi';

interface Prefill {
  title?: string;
  description?: string;
  priority?: string;
}

/**
 * Nuovo task: solo l'essenziale. Titolo, assegnato a (default: chi crea),
 * scadenza, promemoria e ricorrenza facoltativi, priorità Normale/Alta,
 * descrizione facoltativa, cliente in fondo. Il progetto non si sceglie qui:
 * arriva già impostato quando si crea dalla kanban di progetto.
 * Salva con Ctrl/Cmd+Invio.
 */
export function NewTaskModal({ onClose, projectId, prefill }: { onClose: () => void; projectId?: string; prefill?: Prefill }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [form, setForm] = useState({
    title: prefill?.title ?? '',
    description: prefill?.description ?? '',
    assigneeId: me?.id ?? '',
    dueDate: '',
    remindAt: '',
    recurrence: 'NONE' as TaskRecurrenceKey,
    high: prefill?.priority === 'ALTA',
    clientId: '',
  });

  const { data: clients } = useQuery(clientsLiteQuery);
  const { data: users } = useQuery(assignableUsersQuery);

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/tasks', {
      title: form.title.trim(),
      description: form.description.trim() || null,
      projectId: projectId ?? null,
      clientId: form.clientId || null,
      status: 'TODO',
      priority: form.high ? 'ALTA' : 'MEDIA',
      assigneeId: form.assigneeId || null,
      dueDate: form.dueDate || null,
      remindAt: fromDateTimeInput(form.remindAt),
      recurrence: form.recurrence,
      isPrivate: false,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.ok('Task creato', form.title);
      onClose();
    },
    onError: (err) => toast.alert('Task non creato', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.title.trim() && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title="Nuovo task"
      kicker="Task"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            Crea task <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Titolo" required span={12}>
          <input className="inp" autoFocus placeholder="Cosa va fatto" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Assegnato a" span={6}>
          <select className="inp" value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
            <option value="">Nessuno</option>
            {(users ?? []).map((u) => <option key={u.id} value={u.id}>{fullName(u)}{u.id === me?.id ? ' (io)' : ''}</option>)}
          </select>
        </Field>
        <Field label="Scadenza" span={6}>
          <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </Field>
        <Field label="Promemoria" span={6} hint="Facoltativo, con l'ora">
          <input className="inp mono" type="datetime-local" value={form.remindAt} onChange={(e) => setForm({ ...form, remindAt: e.target.value })} />
        </Field>
        <Field label="Ricorrenza" span={6} hint="Facoltativa: al Completa si crea il prossimo">
          <select className="inp" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value as TaskRecurrenceKey })}>
            {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABEL[r]}</option>)}
          </select>
        </Field>
        <Field label="Priorità" span={12}>
          <div className="seg-control">
            <button type="button" aria-pressed={!form.high} onClick={() => setForm({ ...form, high: false })}>Normale</button>
            <button type="button" aria-pressed={form.high} onClick={() => setForm({ ...form, high: true })}>Alta</button>
          </div>
        </Field>
        <Field label="Descrizione" span={12}>
          <textarea className="inp" rows={3} placeholder="Facoltativa: dettagli utili a chi lo farà" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label="Cliente" span={12} hint="Facoltativo">
          <select className="inp" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
            <option value="">Nessuno</option>
            {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
